import { createUnifiedTheme, defaultComponentThemes, palettes, type PageTheme } from '@backstage/theme';
import { MANEWCO_DARK, MANEWCO_LIGHT, type ManewcoTokens } from '@platform/plugin-platform-react';

/**
 * Jetons Manewco (clair : maquette « Clarté complète », variante contrastée, 2026-10-15, sur la
 * charte de manewco.com #014247 / #069EAA, en Arial ; sombre : dérivé de la même charte). Ils sont
 * définis dans le socle (`@platform/plugin-platform-react`, `manewcoTokens.ts`) pour que les
 * plugins les lisent sans importer l'application, et réexportés ici.
 */
export { MANEWCO_DARK, MANEWCO_LIGHT };
export type { ManewcoTokens };

type Slot = object | ((args: any) => object);
const resolve = (v: Slot | undefined, args: any) => (typeof v === 'function' ? v(args) : v ?? {});

/**
 * Ajoute nos surcharges à celles de Backstage, emplacement par emplacement : `createUnifiedTheme`
 * fusionne les composants en surface, si bien qu'une clé `Mui*` remplacerait sinon tous les
 * réglages par défaut (marges des étiquettes, densité des tableaux, cartes en colonne…).
 */
const extend = (name: string, overrides: Record<string, Slot>) => {
  const base = ((defaultComponentThemes as any)?.[name]?.styleOverrides ?? {}) as Record<string, Slot>;
  const keys = new Set([...Object.keys(base), ...Object.keys(overrides)]);
  return {
    styleOverrides: Object.fromEntries(
      [...keys].map(k => [k, (args: any) => ({ ...resolve(base[k], args), ...resolve(overrides[k], args) })]),
    ),
  };
};

const PAGE_THEME_IDS = [
  'home', 'documentation', 'tool', 'service', 'website', 'library', 'other',
  'app', 'apis', 'card', 'system', 'resource', 'group', 'user', 'domain',
];

/**
 * Thème Manewco construit sur un jeu de jetons : `MANEWCO_LIGHT` donne le rendu de la maquette,
 * `MANEWCO_DARK` le mode sombre. Les jetons sont aussi déposés dans `palette.manewco`, d'où
 * `useManewcoTokens()` les relit pour le thème actif.
 */
export const createManewcoTheme = (MANEWCO: ManewcoTokens) => {
  /** En-tête clair, identique pour toutes les pages (maquette) : plus de bandeau en dégradé. */
  const header: PageTheme = {
    colors: [MANEWCO.background, MANEWCO.background],
    shape: 'none',
    backgroundImage: 'none',
    fontColor: MANEWCO.text,
  };
  return createUnifiedTheme({
    palette: {
      ...(MANEWCO.mode === 'dark' ? palettes.dark : palettes.light),
      // Lu par useManewcoTokens() / manewcoTokens(theme).
      // (clé propre à Manewco, hors du type PaletteOptions de MUI : d'où le spread)
      ...({ manewco: MANEWCO } as object),
      // Les étiquettes des plugins (StatusPill) écrivent en « main » sur fond « light » :
      // texte foncé sur fond pâle, contraste ≥ 4,5:1 (WCAG AA), vérifié par le test du thème.
      primary: { main: MANEWCO.primary, light: MANEWCO.pale, contrastText: MANEWCO.onPrimary },
      secondary: { main: MANEWCO.secondary },
      // Étiquettes de la maquette (fond pâle, sans liseré) : contraste ≥ 5:1.
      success: { ...MANEWCO.tones.success },
      warning: { ...MANEWCO.tones.warning },
      error: { ...MANEWCO.tones.error },
      info: { ...MANEWCO.tones.info },
      background: { default: MANEWCO.background, paper: MANEWCO.surface },
      text: { primary: MANEWCO.text, secondary: MANEWCO.textSecondary },
      divider: MANEWCO.line,
      // Pas de lien cyan : #069EAA ne fait que 3,24:1 sur blanc.
      link: MANEWCO.primary,
      navigation: {
        background: MANEWCO.navBackground,
        indicator: MANEWCO.secondary,
        color: MANEWCO.navText,
        selectedColor: MANEWCO.primary,
        navItem: { hoverBackground: MANEWCO.hover },
        submenu: { background: MANEWCO.navSubmenu },
      },
    },
    fontFamily: MANEWCO.fontFamily,
    // Échelle de la maquette : titre de page 31, bandeau 25, cartes 17, sous-titres 14, texte 14/12.
    typography: {
      htmlFontSize: 16,
      fontFamily: MANEWCO.fontFamily,
      h1: { fontSize: 31, fontWeight: 700, letterSpacing: -1, marginBottom: 8 },
      h2: { fontSize: 25, fontWeight: 700, letterSpacing: -0.5, marginBottom: 8 },
      h3: { fontSize: 20, fontWeight: 700, marginBottom: 6 },
      h4: { fontSize: 17, fontWeight: 700, letterSpacing: -0.2, marginBottom: 4 },
      h5: { fontSize: 17, fontWeight: 700, letterSpacing: -0.2, marginBottom: 4 },
      h6: { fontSize: 14, fontWeight: 700, marginBottom: 2 },
      body1: { fontSize: 14, lineHeight: 1.5 },
      body2: { fontSize: 12, lineHeight: 1.6 },
      subtitle1: { fontSize: 14, fontWeight: 600 },
      subtitle2: { fontSize: 12, fontWeight: 600 },
      caption: { fontSize: 11 },
    } as any,
    defaultPageTheme: 'home',
    pageTheme: Object.fromEntries(PAGE_THEME_IDS.map(id => [id, header])),
    components: {
      BackstageHeader: {
        styleOverrides: {
          header: { backgroundColor: 'transparent', boxShadow: 'none', padding: '28px 32px 16px' },
          title: { color: MANEWCO.text, fontSize: '1.95rem', fontWeight: 700, letterSpacing: -1 },
          subtitle: { color: MANEWCO.textSecondary, opacity: 1, fontSize: 12, fontWeight: 400 },
          type: { color: MANEWCO.textSecondary, opacity: 1, fontWeight: 700, letterSpacing: 1.7, fontSize: 10 },
          breadcrumb: { color: MANEWCO.textSecondary },
        },
      },
      BackstageHeaderTabs: {
        styleOverrides: {
          tabsWrapper: { backgroundColor: MANEWCO.surface, borderBottom: `1px solid ${MANEWCO.line}` },
          defaultTab: {
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            color: MANEWCO.textSecondary,
            padding: '16px 14px',
          },
          selected: { color: MANEWCO.primary },
          tabRoot: { '&:hover': { backgroundColor: MANEWCO.hover, color: MANEWCO.text } },
        },
      },
      // Couleur seulement : une hauteur imposée écraserait celle des onglets verticaux.
      MuiTabs: extend('MuiTabs', { indicator: { backgroundColor: MANEWCO.secondary } }),
      BackstageSidebar: { styleOverrides: { drawer: { borderRight: `1px solid ${MANEWCO.line}` } } },
      BackstageSidebarItem: {
        styleOverrides: {
          // Entrées de la maquette : pastille arrondie, 12 px, icône compacte à gauche.
          root: { margin: '3px 12px', width: 'calc(100% - 24px)', borderRadius: 9, minHeight: 39 },
          iconContainer: { width: 40, marginRight: 0 },
          label: { fontSize: 12, fontWeight: 400, width: 'auto', minWidth: 0 },
          // Zone de badge réduite à son contenu : elle réservait 48 px vides à droite de chaque entrée.
          secondaryAction: { width: 'auto', minWidth: 8 },
          selected: { backgroundColor: MANEWCO.pale, fontWeight: 700 },
        },
      },
      MuiPaper: extend('MuiPaper', {
        // 9 px pour les menus, popovers et dialogues ; les cartes (MuiCard) ont 16 px.
        rounded: { borderRadius: MANEWCO.radius },
        // Cartes seulement : les menus et popovers (elevation8) gardent leur ombre.
        elevation1: { boxShadow: MANEWCO.shadow, border: `1px solid ${MANEWCO.line}` },
      }),
      MuiCard: extend('MuiCard', {
        root: { borderRadius: MANEWCO.radiusCard, border: `1px solid ${MANEWCO.line}`, boxShadow: MANEWCO.shadow },
      }),
      // Les listes d'autocomplétion (filtres du catalogue) utilisent un Paper elevation1 : sans
      // cela, elles prendraient la bordure plate des cartes et se confondraient avec la page.
      MuiAutocomplete: { styleOverrides: { paper: { border: 'none', boxShadow: MANEWCO.popoverShadow } } },
      MuiButton: extend('MuiButton', {
        root: { borderRadius: MANEWCO.radius, textTransform: 'none', fontWeight: 600, fontSize: 12, minHeight: 38, padding: '8px 14px' },
        containedPrimary: {
          backgroundImage: 'none',
          backgroundColor: MANEWCO.primary,
          '&:hover': { backgroundColor: MANEWCO.primaryHover },
        },
        outlined: { borderColor: MANEWCO.outline },
      }),
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: MANEWCO.radius },
          notchedOutline: { borderColor: MANEWCO.fieldLine },
        },
      },
      MuiTableCell: extend('MuiTableCell', {
        head: { backgroundColor: MANEWCO.tableHead, color: MANEWCO.tableHeadText, fontWeight: 600 },
        root: { borderBottom: `1px solid ${MANEWCO.line}` },
      }),
      MuiTableRow: extend('MuiTableRow', {
        root: {
          // Lignes alternées très légères (le fond de page, plus foncé, alourdirait les tableaux).
          '&:nth-of-type(odd)': { backgroundColor: MANEWCO.tableRowOdd },
          // Même force que la règle de MUI (`&$hover:hover`), sinon le gris par défaut l'emporte.
          '&$hover:hover': { backgroundColor: MANEWCO.tableRowHover },
        },
      }),
      MuiChip: extend('MuiChip', { root: { fontWeight: 600 } }),
      MuiCardHeader: extend('MuiCardHeader', {
        title: { fontSize: 17, fontWeight: 700, letterSpacing: -0.2 },
        subheader: { fontSize: 11, color: MANEWCO.textSecondary },
      }),
    },
  });
};

export const manewcoTheme = createManewcoTheme(MANEWCO_LIGHT);
export const manewcoDarkTheme = createManewcoTheme(MANEWCO_DARK);
